import axios from "axios";
import { ApiException } from "@/services/api/ApiExceptions";
import {
  IElectronicSignature,
  IObrigatorySignature,
  ISimpleFile,
  ITokenCapacity,
  IExternalSubscriber,
  ITokenSignature,
  IInternalSignature,
  IRefuseSignature,
  ISignatureId,
  IRefuseSignatureId,
  ISignatureObject
} from "@/store/types/signature-type";
import { FilterSignaturePaged } from "@/store/types/types";
import { IProcessFileId, IValidatedFile } from "@/store/types/file-type";

// start eletronic signature
export const startElectronicSignatureRequest = (signature: ISignatureObject) =>
  axios({
    url: "process/file/signature/start-eletronic-sign",
    data: signature,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// eletronic signature // cpf, password and process file id
export const requestTokenElectronicRequest = ({
  cpf,
  password,
  processFileId,
  capacity,
  send_by
}: IElectronicSignature) =>
  axios({
    url: "process/file/signature/eletronic-token",
    data: {
      cpf,
      password,
      process_file: processFileId,
      capacity,
      send_by
    },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// eletronic signature check token
export const checkTokenElectronicRequest = ({
  token,
  capacity
}: ITokenCapacity) =>
  axios({
    url: "process/file/signature/validate-eletronic-token",
    data: {
      token,
      capacity
    },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getRequiredSignatureFiles = ({
  page,
  order_by,
  ordenation,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  only_count
}: FilterSignaturePaged) =>
  axios({
    url: `process/file/signature/list`,
    method: "GET",
    params: {
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      only_count
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Signature End Points
export const setSignatureRequest = ({
  subscribers,
  hasOrder,
  processId,
  joinId
}: IInternalSignature) =>
  axios({
    url: "process/file/signature",
    data: subscribers,
    params: {
      follow_order: hasOrder,
      process_id: processId,
      document_join_id: joinId
    },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getRequestedSignatureFiles = ({
  page,
  order_by,
  ordenation,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  only_count
}: FilterSignaturePaged) =>
  axios({
    url: `process/file/signature/requested/list`,
    method: "GET",
    params: {
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      only_count
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSignedSignatureFiles = ({
  page,
  order_by,
  ordenation,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  only_count
}: FilterSignaturePaged) =>
  axios({
    url: `process/file/signature/answered/list`,
    method: "GET",
    params: {
      page,
      order_by,
      ordenation,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      only_count
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setExternalSignatureRequest = (
  subscriber: IExternalSubscriber,
  process_id: string
) =>
  axios({
    url: `/process/file/signature-external`,
    params: { process_id },
    data: subscriber,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getExternalSignatureData = (hash: string) =>
  axios({
    url: `/process/file/signature-external/validate/`,
    params: { hash },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getExternalSignatureToken = (hash: string) =>
  axios({
    url: `/process/file/signature-external/token/`,
    params: { hash },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const postExternalSignatureResponse = (
  hash: string,
  { process_file_id, token }: ITokenSignature
) =>
  axios({
    url: `/process/file/signature-external/response/`,
    params: { hash },
    data: { process_file_id, token },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const refuseExternalSignature = (
  hash: string,
  { refuse_justification, refuse_option }: IRefuseSignature
) =>
  axios({
    url: `/process/file/signature-external/refuse/`,
    params: { hash },
    data: { refuse_justification, refuse_option },
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDocumentPrintVersion = ({
  process_file_id,
  process_id = ""
}: IProcessFileId) =>
  axios({
    url: `process/file/download/printerversion/${process_file_id}/`,
    method: "GET",
    params: { process_id }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const requestSignatureTokens = (fileIds: IProcessFileId[]) =>
  axios({
    url: "process/file/signature/token",
    data: fileIds,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const signatureTokensRequest = (signatureList: ISignatureObject) =>
  axios({
    url: "process/file/signature/response",
    data: signatureList,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const refuseSignatureFile = (signature: IRefuseSignatureId[]) =>
  axios({
    url: `process/file/signature/refuse`,
    data: signature,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// cancel signature file
export const cancelSignatureFile = (list_signature: ISignatureId[]) =>
  axios({
    url: "process/file/signature/cancel",
    data: list_signature,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// remove signature file
export const removeSignatureFile = ({
  processFileId,
  processId
}: ISimpleFile) =>
  axios({
    url: `process/file/signature/delete/${processFileId}`,
    method: "DELETE",
    data: { process_id: processId }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// create obrigatory signature file doe
export const createDoeObrigatorySignature = (
  listSignature: IObrigatorySignature[]
) =>
  axios({
    url: `process/obrigatory_signature/create`,
    method: "POST",
    data: listSignature
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// delete obrigatory signature file doe
export const deleteDoeObrigatorySignatureRequest = (signatureId: string) =>
  axios({
    url: `process/obrigatory_signature/delete/${signatureId}`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const readNotificationRequest = (process_id: string) =>
  axios({
    url: `process/file/signature-visualize/${process_id}`,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// authenticate file
export const authenticateFile = (data: IValidatedFile[]) =>
  axios({
    url: "process/file/authenticate",
    data,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const checkSignatureProcessFileRequest = (process_file_id?: string) =>
  axios({
    url: `process/file/signature/check-signature`,
    method: "GET",
    params: { process_file_id }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });
